import React from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';

interface Props {
  layer: number;
  /** The first stage has no barrier above it. */
  first: boolean;
  summary?: string | null;
  current?: boolean;
  skipped?: boolean;
  /** Controls for the stage itself, set beside its heading by the editor. */
  actions?: React.ReactNode;
  children: React.ReactNode;
}

// One stage as a list item: the barrier above it, its heading, and its steps. Shared by the
// stage view and the editor, so a workflow looks the same whether it is read or edited.
const WorkflowStageFrame: React.FC<Props> = ({
  layer,
  first,
  summary,
  current = false,
  skipped = false,
  actions,
  children,
}) => (
  <Box component="li" aria-current={current ? 'step' : undefined}>
    {!first && (
      <Divider sx={{ my: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          all steps done before next stage
        </Typography>
      </Divider>
    )}
    <Box
      sx={{
        borderLeft: 3,
        borderColor: current ? 'primary.main' : 'transparent',
        bgcolor: current ? 'action.hover' : 'transparent',
        borderRadius: 1,
        p: 1,
      }}
    >
      <Stack
        direction="row"
        useFlexGap
        flexWrap="wrap"
        spacing={1}
        alignItems="baseline"
        sx={{ mb: 1 }}
      >
        <Typography
          variant="subtitle2"
          component="h3"
          sx={{ color: skipped ? 'text.disabled' : 'text.primary' }}
        >
          Stage {layer}
        </Typography>
        {summary && (
          <Typography variant="caption" color="text.secondary">
            {summary}
          </Typography>
        )}
        {current && (
          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
            Here now
          </Typography>
        )}
        {skipped && (
          <Typography variant="caption" color="text.disabled">
            Skipped by a decision
          </Typography>
        )}
        {actions && <Box sx={{ ml: 'auto' }}>{actions}</Box>}
      </Stack>
      <Stack spacing={1}>{children}</Stack>
    </Box>
  </Box>
);

export default WorkflowStageFrame;
